// src/components/analytics/TakeoffLandingAnalytics.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Plane, ArrowUpRight, ArrowDownLeft,
  RefreshCw, Calendar, Filter, ChevronDown, Clock, DollarSign,
  BarChart2, Activity, Globe, Users, Target, AlertCircle, CheckCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Tiny SVG chart helpers ───────────────────────────────────────────────────

const SparkLine = ({ data = [], color = '#38bdf8', height = 50, width = 200 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${pts.join(' ')} ${width},${height}`}
        fill={`url(#sg-${color.replace('#','')})`}
      />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

const MiniBar = ({ value, max, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(value / max) * 100}%`, background: color }}
      />
    </div>
  </div>
);

const DonutChart = ({ visitors = 0, clients = 0, size = 120 }) => {
  const total = visitors + clients || 1;
  const r = 42;
  const circ = 2 * Math.PI * r;
  const vAngle = (visitors / total) * circ;
  const cAngle = (clients / total) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="14" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#38bdf8" strokeWidth="14"
        strokeDasharray={`${vAngle} ${circ}`} strokeDashoffset={circ * 0.25}
        strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#f59e0b" strokeWidth="14"
        strokeDasharray={`${cAngle} ${circ}`} strokeDashoffset={circ * 0.25 - vAngle}
        strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
      <text x="50" y="45" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">{total}</text>
      <text x="50" y="60" textAnchor="middle" fill="#94a3b8" fontSize="8">TOTAL</text>
    </svg>
  );
};

const BarChartSVG = ({ data = [], colorA = '#38bdf8', colorB = '#f59e0b', labelKey = 'label', aKey = 'takeoffs', bKey = 'landings' }) => {
  const maxVal = Math.max(...data.flatMap(d => [d[aKey] || 0, d[bKey] || 0]), 1);
  const W = 340, H = 100, pad = 20, barW = Math.max(4, (W - pad * 2) / (data.length * 2.5));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 20}`} className="overflow-visible">
      {[0, 0.5, 1].map(t => (
        <line key={t} x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
          stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      ))}
      {data.map((d, i) => {
        const x = pad + i * ((W - pad * 2) / data.length);
        const hA = ((d[aKey] || 0) / maxVal) * H;
        const hB = ((d[bKey] || 0) / maxVal) * H;
        return (
          <g key={i}>
            <rect x={x} y={H - hA} width={barW} height={hA} fill={colorA} rx="2" opacity="0.85" />
            <rect x={x + barW + 2} y={H - hB} width={barW} height={hB} fill={colorB} rx="2" opacity="0.85" />
            <text x={x + barW} y={H + 14} textAnchor="middle" fill="#64748b" fontSize="7">
              {d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Mock data generator (replace with real API calls) ───────────────────────

const generateMockData = (days = 30) => {
  const trendData = Array.from({ length: days }, (_, i) => ({
    day: `Day ${i + 1}`,
    label: `D${i + 1}`,
    takeoffs: Math.floor(60 + Math.random() * 60),
    landings: Math.floor(58 + Math.random() * 62),
  }));

  const byCategory = [
    { label: 'Charter', takeoffs: 163, landings: 129, visitors: 163, clients: 129 },
    { label: 'Private', takeoffs: 335, landings: 310, visitors: 335, clients: 310 },
    { label: 'Commercial', takeoffs: 220, landings: 215, visitors: 220, clients: 215 },
    { label: 'Other', takeoffs: 88, landings: 91, visitors: 88, clients: 91 },
  ];

  const topProfiles = [
    { name: 'Airlines A', takeoffs: 2540, landings: 2538 },
    { name: 'Corporate Jet B', takeoffs: 99, landings: 259 },
    { name: 'Private Pilot C', takeoffs: 61, landings: 159 },
    { name: 'Corporate Jet D', takeoffs: 45, landings: 159 },
  ];

  const topByType = [
    { name: 'Corporate Jet', value: 548 },
    { name: 'Leased Aircraft', value: 68 },
    { name: 'Airlines A', value: 51 },
  ];

  return { trendData, byCategory, topProfiles, topByType };
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, sub, icon: Icon, color, trend, sparkData }) => (
  <div className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300">
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" style={{ color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      {sparkData && (
        <div className="mt-2 opacity-70">
          <SparkLine data={sparkData} color={color} height={36} width={160} />
        </div>
      )}
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-emerald-400" />}
          {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
            {sub}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const TakeoffLandingAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [filterType, setFilterType] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [animated, setAnimated] = useState(false);

  // Real data state
  const [movements, setMovements] = useState({ takeoffs: 0, landings: 0 });
  const [trendData, setTrendData] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [topProfiles, setTopProfiles] = useState([]);
  const [topByType, setTopByType] = useState([]);
  const [onTimePerf, setOnTimePerf] = useState(15.68);
  const [avgDelay, setAvgDelay] = useState(-2.4);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Try real API first
      const params = { range: dateRange };
      const [movRes] = await Promise.all([
        axios.get(`${API_BASE}/tracking/movements/stats`, { params }).catch(() => null),
      ]);

      if (movRes?.data) {
        const d = movRes.data;

        setMovements({
          takeoffs: d.total_takeoffs    || 0,
          landings: d.total_landings    || 0,
          visitors: d.visitor_movements || 0,
          clients:  d.client_movements  || 0,
        });

        // Trend: [{day, takeoffs, landings}]
        if (d.trend?.length) {
          setTrendData(d.trend.map(r => ({
            day:      r.day,
            label:    r.day ? r.day.slice(5) : '',
            takeoffs: r.takeoffs || 0,
            landings: r.landings || 0,
          })));
        }

        // Top profiles
        if (d.top_profiles?.length) {
          setTopProfiles(d.top_profiles.map(r => ({
            name:     r.name,
            takeoffs: r.takeoffs || 0,
            landings: r.landings || 0,
          })));
        }

        // by_category si disponible
        if (d.by_category?.length) {
          setByCategory(d.by_category.map(r => ({
            label:    r.category || 'Unknown',
            takeoffs: r.takeoffs || 0,
            landings: r.landings || 0,
            visitors: r.takeoffs || 0,
            clients:  r.landings || 0,
          })));
        }

      } else {
        // Fallback mock
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
        const mock = generateMockData(days);
        const totalTakeoffs = mock.trendData.reduce((s, dd) => s + dd.takeoffs, 0);
        const totalLandings = mock.trendData.reduce((s, dd) => s + dd.landings, 0);
        setMovements({ takeoffs: totalTakeoffs, landings: totalLandings, visitors: 0, clients: 0 });
        setTrendData(mock.trendData);
        setByCategory(mock.byCategory);
        setTopProfiles(mock.topProfiles);
        setTopByType(mock.topByType);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const mock = generateMockData(days);
      const totalTakeoffs = mock.trendData.reduce((s, dd) => s + dd.takeoffs, 0);
      const totalLandings = mock.trendData.reduce((s, dd) => s + dd.landings, 0);
      setMovements({ takeoffs: totalTakeoffs, landings: totalLandings, visitors: 0, clients: 0 });
      setTrendData(mock.trendData);
      setByCategory(mock.byCategory);
      setTopProfiles(mock.topProfiles);
      setTopByType(mock.topByType);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      setTimeout(() => setAnimated(true), 100);
    }
  }, [dateRange]);

  useEffect(() => {
    setAnimated(false);
    fetchData();
  }, [fetchData]);

  const takeoffSparkline = trendData.map(d => d.takeoffs);
  const landingSparkline = trendData.map(d => d.landings);
  const maxProfile = Math.max(...topProfiles.map(p => p.takeoffs), 1);
  const maxByType = Math.max(...topByType.map(p => p.value), 1);

  // Visitors/clients: prefer API direct counts, fallback to byCategory sum
  const totalVisitors = movements.visitors || byCategory.reduce((s, d) => s + (d.visitors || 0), 0);
  const totalClients  = movements.clients  || byCategory.reduce((s, d) => s + (d.clients  || 0), 0);

  return (
    <div className="space-y-4 px-1">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <div className="p-1.5 bg-blue-500/20 rounded-lg">
              <Activity size={18} className="text-blue-400" />
            </div>
            Takeoff &amp; Landing Analytics
          </h2>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()} — Data refresh 2 mins
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range */}
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {['7d', '30d', '90d'].map(r => (
              <button key={r} onClick={() => setDateRange(r)}
                className={`px-3 py-1.5 transition-colors ${dateRange === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '3 Months'}
              </button>
            ))}
          </div>

          {/* Filter */}
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-300 text-xs focus:outline-none">
            <option value="all">All Aircraft</option>
            <option value="visitor">Visitors</option>
            <option value="client">Clients</option>
          </select>

          <button onClick={fetchData}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Row 1: KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Total Kick-offs" value={movements.takeoffs}
          sub={`↑ KICK-OFFS trend`} trend="up" icon={ArrowUpRight} color="#38bdf8"
          sparkData={takeoffSparkline} />
        <StatCard title="Total Landings" value={movements.landings}
          sub={`↓ LANDINGS trend`} trend="down" icon={ArrowDownLeft} color="#f59e0b"
          sparkData={landingSparkline} />
        <StatCard title="On-Time Performance" value={`${onTimePerf.toFixed(2)}%`}
          sub="Avg on-time kicks" trend="up" icon={CheckCircle} color="#10b981" />
        <StatCard title="Avg. Delay" value={`${avgDelay > 0 ? '+' : ''}${avgDelay}%`}
          sub="Avg. delay index" trend={avgDelay < 0 ? 'down' : 'up'} icon={Target} color="#a78bfa" />
      </div>

      {/* ── Row 2: Flight Trends + Distribution + Peak Times ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

        {/* Flight trends */}
        <div className="lg:col-span-2 bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <BarChart2 size={15} className="text-blue-400" />
              Flight Trends (Last {dateRange === '7d' ? '7' : dateRange === '30d' ? '30' : '90'} Days)
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-sky-400 inline-block" />Kick-offs</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block" />Landings</span>
            </div>
          </div>
          <BarChartSVG data={trendData.slice(-20)} colorA="#38bdf8" colorB="#f59e0b"
            labelKey="label" aKey="takeoffs" bKey="landings" />
        </div>

        {/* Distribution donut */}
        <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Globe size={15} className="text-purple-400" />
            Visitor vs Client Split
          </h3>
          <div className="flex items-center justify-center mb-3">
            <DonutChart visitors={totalVisitors} clients={totalClients} size={130} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-sky-400">
                <span className="w-2.5 h-2.5 rounded-full bg-sky-400 inline-block" />
                Visitors
              </span>
              <span className="text-white font-semibold">{totalVisitors.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-amber-400">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                Clients
              </span>
              <span className="text-white font-semibold">{totalClients.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 3: By Category + Top Profiles + Top by Type ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

        {/* By category */}
        <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Filter size={15} className="text-emerald-400" />
            Kick-offs &amp; Landings by Category
          </h3>
          <div className="space-y-3">
            {byCategory.map(cat => (
              <div key={cat.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-300 font-medium">{cat.label}</span>
                  <div className="flex gap-3">
                    <span className="text-sky-400 tabular-nums">{cat.takeoffs.toLocaleString()}</span>
                    <span className="text-amber-400 tabular-nums">{cat.landings.toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-400 rounded-full transition-all duration-700"
                      style={{ width: animated ? `${(cat.takeoffs / Math.max(...byCategory.map(c => c.takeoffs))) * 100}%` : '0%' }} />
                  </div>
                  <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all duration-700"
                      style={{ width: animated ? `${(cat.landings / Math.max(...byCategory.map(c => c.landings))) * 100}%` : '0%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 border-t border-gray-700 pt-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400" />Visitors: {totalVisitors.toLocaleString()}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Clients: {totalClients.toLocaleString()}</span>
          </div>
        </div>

        {/* Top 5 visitor & client profiles */}
        <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Users size={15} className="text-pink-400" />
            Top Visitor &amp; Client Profiles
          </h3>
          <div className="space-y-3">
            {topProfiles.map((p, i) => (
              <div key={p.name} className="group">
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 w-4 text-right">{i + 1}</span>
                    <span className="text-gray-300 truncate max-w-[100px]">{p.name}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-sky-400 tabular-nums">{p.takeoffs.toLocaleString()}</span>
                    <span className="text-amber-400 tabular-nums">{p.landings.toLocaleString()}</span>
                  </div>
                </div>
                <MiniBar value={p.takeoffs} max={maxProfile}
                  color={`hsl(${200 + i * 20}, 80%, 60%)`} />
              </div>
            ))}
          </div>
        </div>

        {/* Top by aircraft type + Peak times */}
        <div className="space-y-3">
          <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <Plane size={15} className="text-amber-400" />
              Top Landing Tax Generators
            </h3>
            <div className="space-y-2">
              {topByType.map((t, i) => (
                <div key={t.name} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 w-4 text-right">{i + 1}</span>
                  <span className="text-gray-300 flex-1 truncate">{t.name}</span>
                  <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all duration-700"
                      style={{ width: animated ? `${(t.value / maxByType) * 100}%` : '0%' }} />
                  </div>
                  <span className="text-amber-400 tabular-nums w-8 text-right">{t.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Peak times mini charts */}
          <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
              <Clock size={15} className="text-cyan-400" />
              Peak Times
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-sky-400 mb-1">Peak Times for Kick-offs</p>
                <SparkLine
                  data={[80, 120, 200, 180, 250, 220, 190, 160, 140, 100, 90, 70]}
                  color="#38bdf8" height={36} width={240} />
                <div className="flex justify-between text-xs text-gray-600 mt-0.5">
                  <span>0:00</span><span>6:00</span><span>12:00</span><span>18:00</span><span>Day</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-amber-400 mb-1">Peak Times for Landings</p>
                <SparkLine
                  data={[60, 100, 160, 200, 290, 260, 210, 180, 150, 110, 80, 60]}
                  color="#f59e0b" height={36} width={240} />
                <div className="flex justify-between text-xs text-gray-600 mt-0.5">
                  <span>0:00</span><span>6:00</span><span>12:00</span><span>18:00</span><span>Day</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 4: Recent movements table ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Activity size={15} className="text-green-400" />
            Recent Movements
          </h3>
          <span className="text-xs text-gray-500">Live feed</span>
        </div>
        <RecentMovementsTable filterType={filterType} />
      </div>

    </div>
  );
};

// ─── Recent Movements sub-component ──────────────────────────────────────────

const RecentMovementsTable = ({ filterType }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/tracking/movements/recent?limit=20`);
        setMovements(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Mock fallback avec les nouveaux champs
        const mock = Array.from({ length: 12 }, (_, i) => ({
          visit_id: i,
          tail_number: `N${100 + Math.floor(Math.random() * 900)}XY`,
          movement_type: Math.random() > 0.5 ? 'TAKEOFF' : 'LANDING',
          timestamp: new Date(Date.now() - Math.random() * 86400000 * 3).toISOString(),
          is_billable: Math.random() > 0.3,
          source: Math.random() > 0.5 ? 'visitor' : 'registered',
          speed: Math.floor(80 + Math.random() * 120),
          altitude: Math.floor(500 + Math.random() * 3000),
          aircraft_type: ['Corporate Jet', 'Piston', 'Turboprop', null][Math.floor(Math.random() * 4)],
          engine_type: ['Jet', 'Piston', 'Turboprop', null][Math.floor(Math.random() * 4)],
          mtow_kg: Math.random() > 0.4 ? Math.floor(3000 + Math.random() * 30000) : null,
          fee: Math.random() > 0.3 ? parseFloat((50 + Math.random() * 150).toFixed(2)) : 0,
          operator_name: Math.random() > 0.5 ? ['Delta Air', 'Southwest', 'Private Corp'][Math.floor(Math.random() * 3)] : null,
        }));
        setMovements(mock);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = filterType === 'all' ? movements
    : movements.filter(m => m.source === filterType || (filterType === 'visitor' && m.source === 'visitor') || (filterType === 'client' && m.source === 'registered'));

  if (loading) return (
    <div className="flex items-center justify-center py-6">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400" />
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b border-gray-700">
            <th className="text-left py-2 px-3">Tail</th>
            <th className="text-left py-2 px-3">Source</th>
            <th className="text-left py-2 px-3">Movement</th>
            <th className="text-left py-2 px-3">Model Type</th>
            <th className="text-left py-2 px-3">Engine Type</th>
            <th className="text-left py-2 px-3">MTW (Lbs)</th>
            <th className="text-left py-2 px-3">Fee</th>
            <th className="text-left py-2 px-3">Op Name</th>
            <th className="text-left py-2 px-3">Timestamp</th>
            <th className="text-left py-2 px-3">Speed</th>
            <th className="text-left py-2 px-3">Billable</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {filtered.slice(0, 20).map((m, i) => (
            <tr key={m.visit_id ?? i} className="hover:bg-gray-700/30 transition-colors">
              <td className="py-2 px-3 font-mono font-bold text-white">{m.tail_number}</td>
              <td className="py-2 px-3">
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  m.source === 'visitor' ? 'bg-sky-500/20 text-sky-400' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {m.source === 'visitor' ? 'Visitor' : 'Client'}
                </span>
              </td>
              <td className="py-2 px-3">
                <span className={`flex items-center gap-1 font-medium ${
                  m.movement_type === 'TAKEOFF' ? 'text-emerald-400' : 'text-orange-400'
                }`}>
                  {m.movement_type === 'TAKEOFF'
                    ? <><ArrowUpRight size={12} />Kick-off</>
                    : <><ArrowDownLeft size={12} />Landing</>}
                </span>
              </td>
              <td className="py-2 px-3 text-gray-300">{m.aircraft_type || '—'}</td>
              <td className="py-2 px-3 text-gray-300">{m.engine_type || '—'}</td>
              <td className="py-2 px-3 text-gray-300 tabular-nums">
                {m.mtow_kg ? Math.round(m.mtow_kg * 2.20462).toLocaleString() : '—'}
              </td>
              <td className="py-2 px-3 tabular-nums">
                {m.fee > 0
                  ? <span className="text-emerald-400 font-medium">${m.fee.toFixed(2)}</span>
                  : <span className="text-gray-500">—</span>}
              </td>
              <td className="py-2 px-3 text-gray-300 max-w-[120px] truncate">
                {m.operator_name || <span className="text-gray-600 italic">Unregistered</span>}
              </td>
              <td className="py-2 px-3 text-gray-400 tabular-nums">
                {m.timestamp ? new Date(m.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </td>
              <td className="py-2 px-3 text-gray-300 tabular-nums">{m.speed ? `${Math.round(m.speed)} kts` : '—'}</td>
              <td className="py-2 px-3">
                {m.is_billable
                  ? <CheckCircle size={13} className="text-emerald-400" />
                  : <AlertCircle size={13} className="text-gray-600" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">No movements found</div>
      )}
    </div>
  );
};

export default TakeoffLandingAnalytics;